// frontend/src/components/staff/scheduleAuditUtils.js
//
// Workforce — SCHED-SLICE1-F audit trail viewer helpers.
//
// Pure functions for rendering, filtering, and formatting AuditLog rows for
// the workforce/scheduling Slice 1 audit surface. Loosely coupled to the
// `AuditLog` schema (locked at the contract level in
// docs/architecture/workforce-scheduling-tech.md §"AuditLog — extension for
// workforce scheduling"):
//
//   action enum (13 values; 6 active in Slice 1):
//     shift_created, shift_edited, shift_deleted, shift_published,
//     shift_cancelled, schedule_published,
//     availability_set, availability_deleted,
//     swap_request, swap_approve, swap_reject,           // Slice 2 (forward-compat)
//     time_off_requested, time_off_approved, time_off_rejected // Slice 2 (forward-compat)
//
//   metadata shapes (per Marcus's tech doc table):
//     shift_created      : { shiftId, staffProfileId, startTime, endTime, roleCode, status }
//     shift_edited       : { shiftId, before: {...}, after: {...} }
//     shift_deleted      : { shiftId, before: {...} }
//     shift_published    : { shiftId, publishedAt, staffProfileId }
//     shift_cancelled    : { shiftId, cancelledAt, reason }
//     schedule_published : { shiftIds: [...], publishedAt, dateRange: { from, to } }
//
// All timestamps are rendered in America/Phoenix per CLAUDE.md global rule.
// All times labeled "MST" explicitly.

import moment from "moment-timezone";

export const STUDIO_TZ = "America/Phoenix";

/**
 * Filter categories for the per-week page chips. Maps the visible chip label
 * to the set of `AuditLog.action` values it covers.
 *
 * Slice 1 active filter chips: All, Created, Edited, Cancelled, Published.
 * Slice 2 actions (swap_*, time_off_*) are silently dropped from the visible
 * surface; admin doesn't see Slice 2 events even if Simone's backend emits
 * them during forward-compat testing.
 *
 * Historical note (SCHED-AUDIT-DELETED-RENAME, 2026-05-14): the "Deleted"
 * chip was renamed to "Cancelled" because SCHED-SLICE1-C reshaped the
 * destructive delete path into a soft-cancel that emits
 * `action: 'shift_cancelled'`. The chip's filter set INCLUDES both
 * `shift_cancelled` (current vocabulary) and `shift_deleted` (preserved so
 * any historic rows from pre-SLICE1-C remain reachable from the audit UI).
 * Old `?filter=deleted` URL params remain accepted as an alias for
 * `cancelled` so any pre-rename shared links continue to work.
 */
export const FILTER_CATEGORIES = Object.freeze({
  all: {
    key: "all",
    label: "All",
    actions: [
      "shift_created",
      "shift_edited",
      "shift_deleted",
      "shift_published",
      "shift_cancelled",
      "schedule_published",
    ],
  },
  created: {
    key: "created",
    label: "Created",
    actions: ["shift_created"],
  },
  edited: {
    key: "edited",
    label: "Edited",
    actions: ["shift_edited"],
  },
  cancelled: {
    key: "cancelled",
    label: "Cancelled",
    actions: ["shift_cancelled", "shift_deleted"],
  },
  published: {
    key: "published",
    label: "Published",
    actions: ["shift_published", "schedule_published"],
  },
});

export const FILTER_KEYS = Object.freeze(Object.keys(FILTER_CATEGORIES));

/**
 * Backward-compat alias map for filter-key URL params.
 *
 * `?filter=deleted` is the pre-2026-05-14 spelling — it is silently rewritten
 * to `cancelled` so any shared URLs from the original audit-log rollout still
 * land on the right tab. Add new aliases here if the vocabulary shifts again.
 */
export const FILTER_KEY_ALIASES = Object.freeze({
  deleted: "cancelled",
});

/**
 * Normalize a filter key sourced from a URL param. Accepts any FILTER_KEYS
 * member or a FILTER_KEY_ALIASES entry and returns the canonical key.
 * Returns null for unknown keys (caller should fall back to the default).
 */
export const normalizeFilterKey = (raw) => {
  if (!raw) return null;
  if (FILTER_KEYS.includes(raw)) return raw;
  if (Object.prototype.hasOwnProperty.call(FILTER_KEY_ALIASES, raw)) {
    return FILTER_KEY_ALIASES[raw];
  }
  return null;
};

/**
 * Action-color mapping for the row left-accent stripe.
 *
 * Per Zuri visual spec §7.3:
 *   created   = --status-clocked-in green
 *   edited    = --brand-gold
 *   deleted   = --status-violation red
 *   published = --status-locked purple
 *
 * cancelled rows treated as deleted (same visual rhythm — staff impact).
 */
const ACTION_COLOR_MAP = Object.freeze({
  shift_created: "created",
  shift_edited: "edited",
  shift_deleted: "deleted",
  shift_cancelled: "deleted",
  shift_published: "published",
  schedule_published: "published",
});

/**
 * Action verb mapping for the row title line (e.g., "Imani edited").
 */
const ACTION_VERB_MAP = Object.freeze({
  shift_created: "created",
  shift_edited: "edited",
  shift_deleted: "deleted",
  shift_cancelled: "cancelled",
  shift_published: "published",
  schedule_published: "published schedule",
});

/**
 * Map an AuditLog row's `action` to a stable color key for CSS class lookup.
 * Returns "neutral" for unknown actions (e.g., Slice 2 forward-compat values
 * if the BE leaks them).
 */
export const getActionColorKey = (action) =>
  ACTION_COLOR_MAP[action] || "neutral";

/**
 * Map an AuditLog row's `action` to a verb string for the row title.
 * Returns "modified" for unknown actions.
 */
export const getActionVerb = (action) => ACTION_VERB_MAP[action] || "modified";

/**
 * Format an ISO timestamp string in Phoenix-MST with explicit "MST" label.
 * Returns "—" for null/undefined/invalid inputs.
 *
 * Example: "2026-04-19 14:32 MST"
 */
export const formatAuditTimestamp = (iso) => {
  if (!iso) return "—";
  const m = moment.tz(iso, STUDIO_TZ);
  if (!m.isValid()) return "—";
  return `${m.format("YYYY-MM-DD HH:mm")} MST`;
};

/**
 * Format a shift time (e.g., "8:00 AM MST"). Used in diff displays where the
 * value is a startTime/endTime ISO and we want the time-only representation.
 */
export const formatShiftTime = (iso) => {
  if (!iso) return "—";
  const m = moment.tz(iso, STUDIO_TZ);
  if (!m.isValid()) return "—";
  return `${m.format("h:mm A")} MST`;
};

/**
 * Format a weekStart date for the H1 of the per-week page.
 * Example: "Apr 21 - Apr 27, 2026"
 *
 * Accepts either an ISO date string or a YYYY-MM-DD string.
 */
export const formatWeekRange = (weekStart) => {
  if (!weekStart) return "";
  const start = moment.tz(weekStart, STUDIO_TZ);
  if (!start.isValid()) return "";
  const end = start.clone().add(6, "days");
  if (start.year() === end.year()) {
    return `${start.format("MMM D")} - ${end.format("MMM D, YYYY")}`;
  }
  return `${start.format("MMM D, YYYY")} - ${end.format("MMM D, YYYY")}`;
};

/**
 * Fields recognized inside `metadata.before` / `metadata.after` snapshots for
 * shift_edited rows. Order matters — this is the display order in the diff.
 */
const SHIFT_DIFF_FIELDS = [
  { key: "startTime", label: "Start", formatter: formatShiftTime },
  { key: "endTime", label: "End", formatter: formatShiftTime },
  {
    key: "staffProfileId",
    label: "Staff",
    formatter: (v, ctx) => (ctx && ctx.staffNameById && ctx.staffNameById.get(v)) || String(v || "—"),
  },
  { key: "roleCode", label: "Role", formatter: (v) => v || "—" },
  { key: "status", label: "Status", formatter: (v) => v || "—" },
  { key: "notes", label: "Notes", formatter: (v) => v || "(none)" },
];

/**
 * Compute the field-level diff between before/after snapshots. Returns ONLY
 * fields whose values changed. Each entry: { key, label, oldValue, newValue,
 * oldDisplay, newDisplay }.
 *
 * @param {object} before - the pre-edit snapshot (may be empty/null)
 * @param {object} after  - the post-edit snapshot (may be empty/null)
 * @param {object} ctx    - render context (e.g., { staffNameById: Map })
 */
export const computeDiff = (before, after, ctx = {}) => {
  const b = before || {};
  const a = after || {};
  const out = [];
  for (const field of SHIFT_DIFF_FIELDS) {
    const oldValue = b[field.key];
    const newValue = a[field.key];
    if (oldValue === newValue) continue;
    if (oldValue == null && newValue == null) continue;
    out.push({
      key: field.key,
      label: field.label,
      oldValue,
      newValue,
      oldDisplay: field.formatter(oldValue, ctx),
      newDisplay: field.formatter(newValue, ctx),
    });
  }
  return out;
};

/**
 * Filter audit rows by a chip key (one of FILTER_KEYS). Unknown key falls
 * back to "all".
 *
 * @param {Array<{action: string}>} rows
 * @param {string} filterKey
 */
export const filterAuditRows = (rows, filterKey) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const cat = FILTER_CATEGORIES[filterKey] || FILTER_CATEGORIES.all;
  const allowed = new Set(cat.actions);
  return safeRows.filter((r) => r && allowed.has(r.action));
};

/**
 * Sort audit rows newest-first by `createdAt` (or `timestamp` legacy field).
 * Non-mutating — returns a new array.
 */
export const sortAuditRowsNewestFirst = (rows) => {
  const safeRows = Array.isArray(rows) ? [...rows] : [];
  safeRows.sort((a, b) => {
    const aTs = (a && (a.createdAt || a.timestamp)) || "";
    const bTs = (b && (b.createdAt || b.timestamp)) || "";
    // String comparison on ISO-8601 yields chronological order; reverse it.
    if (aTs > bTs) return -1;
    if (aTs < bTs) return 1;
    return 0;
  });
  return safeRows;
};

/**
 * Resolve an actor display name from an AuditLog row.
 *
 * Backend may return any of:
 *   - row.actorName       — pre-resolved by BE (preferred)
 *   - row.createdByName   — alt naming
 *   - row.createdBy       — ObjectId only (fallback — resolve via ctx.userNameById)
 *
 * Falls back to "Admin" if nothing resolvable.
 */
export const resolveActorName = (row, ctx = {}) => {
  if (!row) return "Admin";
  if (row.actorName) return row.actorName;
  if (row.createdByName) return row.createdByName;
  if (row.createdBy && ctx.userNameById && typeof ctx.userNameById.get === "function") {
    return ctx.userNameById.get(row.createdBy) || "Admin";
  }
  return "Admin";
};

/**
 * Build a short identifier line for a shift (drawer header use case).
 * Example: "Apr 22 8:00 AM - 2:00 PM MST"
 *
 * @param {{startTime?: string, endTime?: string}} shift
 */
export const formatShiftIdentifier = (shift) => {
  if (!shift || !shift.startTime) return "Shift";
  const start = moment.tz(shift.startTime, STUDIO_TZ);
  if (!start.isValid()) return "Shift";
  const startLabel = `${start.format("MMM D h:mm A")}`;
  if (!shift.endTime) return `${startLabel} MST`;
  const end = moment.tz(shift.endTime, STUDIO_TZ);
  if (!end.isValid()) return `${startLabel} MST`;
  return `${startLabel} - ${end.format("h:mm A")} MST`;
};
